// Out of Stock Notifications
//
// Handles the "notify me when back in stock" prompt rendered by
// snippets/product-notify-stock.liquid:
//  - Shows/hides the prompt to match the add-to-cart button's disabled
//    state, including after a variant change.
//  - Posts the submitted email address to the Bloomreach webhook endpoint.
//
// The Bloomreach endpoint does not send Access-Control-Allow-Origin, so a
// normal cross-origin fetch is blocked by the browser's CORS preflight.
// This uses mode: 'no-cors' as a workaround, which avoids the preflight by
// restricting the request to a "simple" one (text/plain body, no custom
// headers). Trade-offs to be aware of:
//  - The response is opaque: we cannot read its status or body, so success
//    here only means "the browser sent the request", not "Bloomreach
//    accepted/processed it". A thrown/rejected fetch is the only failure
//    signal available (e.g. offline, DNS failure, blocked request).
//  - The body is still JSON text, but the Content-Type header is forced to
//    text/plain (application/json is not an allowed no-cors header). If
//    Bloomreach's receiver requires an application/json Content-Type to
//    parse the body, this will need revisiting (e.g. a small server-side
//    proxy that forwards the request with the right headers).
(function () {
  if (window.__notifyStockInitialized) return;
  window.__notifyStockInitialized = true;

  var ENDPOINT_URL =
    "https://uk2-api.eng.bloomreach.com/intg/webhook-handler/v1.0/b473fb34-3388-4094-93a0-e3993a72f3e9/callback";
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function getProductInfo(root) {
    return root.closest("product-info");
  }

  function getAddToCartButton(productInfo) {
    return productInfo && productInfo.querySelector(".product-form__submit");
  }

  function getVariantId(productInfo) {
    var hiddenInput = productInfo && productInfo.querySelector(".product-variant-id");
    if (hiddenInput && hiddenInput.value) return hiddenInput.value;

    return null;
  }

  function setStatus(root, type, message) {
    var statusEl = root.querySelector("[data-notify-stock-status]");
    if (!statusEl) return;

    statusEl.textContent = message || "";
    statusEl.classList.remove(
      "hidden",
      "product-notify-stock__status--success",
      "product-notify-stock__status--error"
    );

    if (!message) {
      statusEl.classList.add("hidden");
      return;
    }

    statusEl.classList.add("product-notify-stock__status--" + type);
    statusEl.focus();
  }

  function setLoading(button, isLoading) {
    button.classList.toggle("loading", isLoading);
    button.disabled = isLoading;

    var spinner = button.querySelector(".loading__spinner");
    if (spinner) spinner.classList.toggle("hidden", !isLoading);
  }

  function submitEmail(root) {
    var productInfo = getProductInfo(root);
    var emailInput = root.querySelector("[data-notify-stock-email]");
    var submitBtn = root.querySelector("[data-notify-stock-submit]");
    var email = (emailInput.value || "").trim();

    if (!EMAIL_PATTERN.test(email)) {
      setStatus(root, "error", root.dataset.langErrorInvalidEmail);
      emailInput.focus();
      return;
    }

    var payload = {
      email: email,
      product_id: root.dataset.productId,
      variant_id: getVariantId(productInfo) || root.dataset.variantId,
      product_title: root.dataset.productTitle,
      product_handle: root.dataset.productHandle,
      product_url: root.dataset.productUrl,
      shop_domain: window.location.hostname,
      source: "out_of_stock_notify_form"
    };

    window.brweb?.identify?.({ email_id: email });

    setLoading(submitBtn, true);
    setStatus(root, "success", "");

    fetch(ENDPOINT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(payload)
    })
      .then(function () {
        // Opaque response: reaching here only means the browser sent the
        // request without a network-level error, not that Bloomreach
        // accepted it. See the file header comment for details.
        setStatus(root, "success", root.dataset.langSuccess);
        emailInput.value = "";
      })
      .catch(function (error) {
        console.error("[notify-stock] submission failed", error);
        setStatus(root, "error", root.dataset.langError);
      })
      .finally(function () {
        setLoading(submitBtn, false);
      });
  }

  function syncVisibility(root, addToCartBtn) {
    var available = !addToCartBtn.hasAttribute("disabled");
    root.classList.toggle("hidden", available);
  }

  function bindForm(root) {
    if (root.dataset.notifyStockBound) return;
    root.dataset.notifyStockBound = "true";

    var emailInput = root.querySelector("[data-notify-stock-email]");
    var submitBtn = root.querySelector("[data-notify-stock-submit]");

    submitBtn.addEventListener("click", function (evt) {
      evt.preventDefault();
      submitEmail(root);
    });

    emailInput.addEventListener("keydown", function (evt) {
      if (evt.key === "Enter") {
        evt.preventDefault();
        submitEmail(root);
      }
    });

    // Inside a modal (e.g. a sold-out row's "Notify me" popup) the modal
    // itself controls when this block is shown, for a specific variant.
    // Syncing visibility to the PDP's main add-to-cart button here would
    // hide/show it based on an unrelated variant selection instead.
    if (root.closest("modal-dialog")) return;

    var productInfo = getProductInfo(root);
    var addToCartBtn = getAddToCartButton(productInfo);
    if (!addToCartBtn) return;

    syncVisibility(root, addToCartBtn);

    new MutationObserver(function () {
      syncVisibility(root, addToCartBtn);
    }).observe(addToCartBtn, {
      attributes: true,
      attributeFilter: ["disabled"]
    });
  }

  function init(scope) {
    (scope || document)
      .querySelectorAll("[data-notify-stock]")
      .forEach(bindForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  // Catch notify-stock blocks rendered later (quick view modals, section
  // reloads in the theme editor, etc).
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches("[data-notify-stock]")) bindForm(node);
        if (node.querySelectorAll) init(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
